<script>
import {
  CdrImg, CdrText, CdrLink, CdrGrid, CdrCard,
} from '@rei/cedar';

export default {
  name: 'MultipleProductRecommendations',
  components: {
    CdrImg, CdrText, CdrLink, CdrGrid, CdrCard,
  },
  props: {
    tagline: {
      type: String,
      default: '',
      required: true,
    },
    items: {
      type: Array,
      default: () => [],
    },
    searchUrl: {
      type: String,
      default: '',
    },
    h1: {
      type: String,
      default: '',
    },
  },
  data: () => ({
    baseClassName: 'multiproduct-component',
  }),
};
</script>

<template>
  <aside
    :aria-labelledby="tagline"
    :class="`${baseClassName}`"
  >
    <cdr-grid :class="`${baseClassName}__header`">
      <cdr-text
        :id="tagline"
        :class="`${baseClassName}__header--title`"
        tag="h3"
      >
        {{ tagline }}
      </cdr-text>
    </cdr-grid>
    <cdr-grid
      :class="`${baseClassName}__items-container`"
      tag="ul"
    >
      <cdr-card
        v-for="item in items"
        :key="item.link"
        :class="`${baseClassName}__column-item`"
        tag="li"
      >
        <cdr-text
          :class="`${baseClassName}__column-item-brand`"
          tag="p"
        >
          {{ item.brand }}
        </cdr-text>
        <p :class="`${baseClassName}__column-item-link`">
          <cdr-link
            :href="item.link"
            :data-analytics-id="`multiprodcomp_${item.name}`"
          >
            {{ item.name }}
          </cdr-link>
        </p>
        <p :class="`${baseClassName}__column-item-image`">
          <cdr-img
            :alt="item.imageAlt"
            :src="item.image"
          />
        </p>
      </cdr-card><!-- end of multiproduct-component__column-item -->
    </cdr-grid>
    <cdr-link
      :href="searchUrl"
      :class="`${baseClassName}__bottom-category`"
    >
      See all {{ h1 ? h1 + ' ' : '' }}on REI.com
    </cdr-link>
  </aside><!-- end of multiproduct-component -->
</template>

<style scoped lang="scss">
@import '@rei/cdr-tokens/dist/rei-dot-com/scss/cdr-tokens.scss';

/* Cedar Base Styles */
@import '@rei/cdr-component-variables/dist/scss/index.scss';

$baseClassName: 'multiproduct-component';

.#{$baseClassName} {
  display: grid;
  grid-template-areas: 'header' 'content'  'category' ;
  margin: $cdr-space-two-x 0 $cdr-space-two-x;

  @include cdr-sm-mq-up {
    grid-template-areas: 'header header header category' 'content content content content';

  }

  .#{$baseClassName}__header {
    grid-area: header;
    grid-auto-flow: column;
    grid-template-columns: 1fr;

    &--title {
      @include cdr-text-utility-sans-strong-300;
      margin-bottom: $cdr-space-three-quarter-x;
    }
  }

  .#{$baseClassName}__bottom-category {
    grid-area: category;

    @include cdr-sm-mq-up {
      justify-self: end;
      align-self: start;
    }
  }

  .#{$baseClassName}__items-container {
    grid-area: content;
    grid-template-columns:  repeat(3, minmax(15.3rem, max-content));
    column-gap: $cdr-space-half-x;
    grid-auto-flow: column;
    overflow: auto;
    margin: 0 0 $cdr-space-half-x;

    @include cdr-sm-mq-up {
      column-gap: $cdr-space-three-quarter-x;
      grid-template-columns: 1fr 1fr 1fr;
      overflow: unset;
      min-height: 30rem;
    }
  }

  .#{$baseClassName}__column-item {
    display: grid;
    grid-template-columns: 1fr;
    grid-template-rows: min-content;
    background: rgba(220, 214, 203, 0.2);
    box-shadow: inset .4rem .4rem 4rem 4rem rgba(0, 0, 0, 0.05);
    padding: 1rem $cdr-space-half-x;
    border-radius: unset;

    @include cdr-sm-mq-up {
      padding: $cdr-space-inset-three-quarter-x-stretch;
    }

    &:hover {
      box-shadow: inset 0 0 8rem #ece6db;
      /* remove the default hover from cdrcard */
      transform: none !important;
      transition: none !important;
    }

    &-brand {
      @include cdr-text-eyebrow-100;
      margin-bottom: 0;
    }

    &-link {
      @include cdr-text-utility-sans-100;
      color: $cdr-color-text-primary;
      text-decoration: underline;
      margin-bottom: $cdr-space-three-quarter-x;
      align-items: baseline;

      @include cdr-sm-mq-up {
        margin-bottom: $cdr-space-one-x;
      }

      a {
        @include cdr-card-link-mixin;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
        text-overflow: ellipsis;
        word-break: break-word;
        white-space: normal;
      }
    }

    &-image {
      display: grid;
      align-items: center;
      min-height: 14.8rem;
      @include cdr-sm-mq-up {
        min-height: 17.2rem;
      }

      img {
        margin: auto;
        max-height: 17.2rem;
        max-width: 100%;
        mix-blend-mode: multiply;
      }
    }
  }
}

</style>
